import { localDate, zonedWallTimeToInstant } from "./time-zone.js";
// The proactive scenario policy of Phase 12.5 (CHT-765; docs/engineering/proactive-checks.md). PURE: no database, no provider, no clock of
// its own. The two proactive jobs (`trip.reminder`, `itinerary-item.weather-check`) read the facts, ask this module, and write an outbox
// row or nothing. A forecast is time-bounded evidence: this policy never changes a trip, an item or a request.
//
// Scenarios:
//   1. An upcoming trip start: one reminder per trip, start date and stage, once the start is within the lead time.
//   2. Unresolved customer actions (a question that waits for the customer, a payment that is due) at that time: the reminder carries a
//      closed code, so staff can see it. The actions are read again at wake time; a resolved action is never reminded.
//   3. Unfinished concierge requests at that time: a staff attention row for the owner (or the queue when nobody owns the request).
//   4. A weather-sensitive item that starts within the horizon: a check, judged against the stored evidence; a MATERIAL risk goes to the
//      customer and, by rule, to the concierge. Trivial change and unchanged risk go nowhere.
/** The version of this policy. Written in every decision row of the two jobs. */
export const PROACTIVE_POLICY_VERSION = "proactive.v1";
// ---- Requests of a trip -------------------------------------------------------------------------
/** A request state that waits for the customer: the customer's open Action. */
export const CUSTOMER_ACTION_REQUEST_STATES = Object.freeze([
    "AWAITING_CUSTOMER",
    "PAYMENT_REQUIRED",
]);
/** A request state that waits for staff: the unfinished concierge work that a trip attention notice is about. */
export const UNFINISHED_REQUEST_STATES = Object.freeze([
    "SUBMITTED",
    "ASSIGNED",
    "IN_PROGRESS",
]);
// ---- Trip reminder timing ------------------------------------------------------------------------
export const TRIP_REMINDER_DEFAULT_LEAD_HOURS = 24;
export const TRIP_REMINDER_MAX_LEAD_HOURS = 168;
/** The closed stage code of a lead time: `T-24H` for the default. A key and a dedupe key carry it. */
export const tripReminderStageOf = (leadHours) => `T-${leadHours}H`;
/** The closed reason code of a reminder row for a lead time, with or without open customer actions. */
export const tripReminderReasonOf = (leadHours, openActions) => openActions ? `T_${leadHours}H_ACTIONS` : `T_${leadHours}H`;
/**
 * `LAFAYETTE_TRIP_REMINDER_LEAD_HOURS`: a whole number of hours from 1 to 168. Unset, empty and `off` give the default of 24 (the variable
 * accepts `off` because the Trigger.dev CLI cannot delete a variable). Anything else is invalid; the refusal names the variable, never the
 * value.
 */
export function parseTripReminderLead(raw) {
    const value = (raw ?? "").trim().toLowerCase();
    if (value === "" || value === "off")
        return { ok: true, hours: TRIP_REMINDER_DEFAULT_LEAD_HOURS };
    if (!/^\d{1,3}$/.test(value))
        return { ok: false, variable: "LAFAYETTE_TRIP_REMINDER_LEAD_HOURS" };
    const hours = Number(value);
    if (hours < 1 || hours > TRIP_REMINDER_MAX_LEAD_HOURS) {
        return { ok: false, variable: "LAFAYETTE_TRIP_REMINDER_LEAD_HOURS" };
    }
    return { ok: true, hours };
}
const HOUR_MS = 3_600_000;
/**
 * The instant a trip starts: local midnight of its start date in the zone of its start (UTC when the trip has no zone, or a zone the
 * runtime does not know). A midnight that does not exist in the zone (a clock that jumps at midnight) uses the first valid time after it,
 * and one that happens twice uses the later.
 */
export function tripStartInstant(startsOn, zone) {
    const date = localDate(startsOn, "startsOn");
    const read = (time, zoneName) => zonedWallTimeToInstant(date, time, zoneName, "later").instant;
    try {
        return read("00:00:00", zone ?? "UTC");
    }
    catch {
        try {
            return read("01:00:00", zone ?? "UTC");
        }
        catch {
            return read("00:00:00", "UTC");
        }
    }
}
/** `NOT_YET`: the start is further away than the lead time. `DUE`: inside the lead time and not started. `STARTED`: the start has passed. */
export function tripReminderTiming(input) {
    const start = tripStartInstant(input.startsOn, input.startTimeZone).getTime();
    const now = input.now.getTime();
    if (now >= start)
        return "STARTED";
    return start - now <= input.leadHours * HOUR_MS ? "DUE" : "NOT_YET";
}
/** The largest UTC offset of any zone: a start date begins up to this many hours BEFORE its UTC midnight. */
const MAX_UTC_OFFSET_HOURS = 14;
/**
 * The calendar dates (UTC, `YYYY-MM-DD`) that bound the trips a reminder sweep must look at. It is a safe window for a database query,
 * and it is tight on purpose, so a sweep does not re-read trips that cannot be due:
 *  - a start date before today (UTC) has started in every zone (the latest start of date D is D 12:00 UTC, in UTC-12);
 *  - a start date later than `now + lead + 14 hours` has not started its lead time in any zone (the earliest start of date D is
 *    D-1 10:00 UTC, in UTC+14).
 * The sweep then decides each trip exactly with `tripReminderTiming`.
 */
export function tripReminderDateWindow(now, leadHours) {
    const iso = (ms) => new Date(ms).toISOString().slice(0, 10);
    return {
        from: iso(now.getTime()),
        to: iso(now.getTime() + (leadHours + MAX_UTC_OFFSET_HOURS) * HOUR_MS),
    };
}
// ---- Weather judgement ---------------------------------------------------------------------------
const FACTOR_CODE = Object.freeze({
    PRECIP_PROBABILITY: "P",
    PRECIP_AMOUNT: "A",
    WIND_GUST: "G",
    THUNDERSTORM: "T",
    HAIL: "H",
});
const FACTOR_ORDER = Object.freeze([
    "PRECIP_PROBABILITY",
    "PRECIP_AMOUNT",
    "WIND_GUST",
    "THUNDERSTORM",
    "HAIL",
]);
/**
 * The fingerprint of a verified condition: the level and the set of deciding factors, as a short closed code (`HIGH.PA`). The numbers are
 * left out on purpose: a forecast that moves from 62% to 64% is the same condition, so it does not notify twice. The fingerprint is a
 * part of the dedupe key of a weather notice.
 */
export function weatherFingerprint(input) {
    const codes = FACTOR_ORDER.filter((factor) => input.factors.includes(factor)).map((factor) => FACTOR_CODE[factor]);
    return codes.length === 0 ? input.level : `${input.level}.${codes.join("")}`;
}
const FACTOR_OF_CODE = Object.freeze(Object.fromEntries(Object.entries(FACTOR_CODE).map(([factor, code]) => [
    code,
    factor,
])));
/** The condition a fingerprint stands for (`HIGH.PA`), or `null` when the text is not a fingerprint. The inverse of `weatherFingerprint`. */
export function parseWeatherFingerprint(text) {
    const [level, codes = "", extra] = text.split(".");
    if (extra !== undefined || level === undefined || !(level in RANK))
        return null;
    const factors = [];
    for (const code of codes) {
        const factor = FACTOR_OF_CODE[code];
        if (factor === undefined)
            return null;
        factors.push(factor);
    }
    return { level: level, factors };
}
/**
 * What a customer (or the concierge) was last TOLD, from the fingerprints of the notices already queued for one item and audience: the
 * highest level, with every factor that was named at any level. `null` when nobody was told. A notice is needed only when the new
 * condition is NEW or WORSE than this (`classifyWeatherChange`), so a SEVERE risk that eases to HIGH with the same factors is not told
 * again, and a risk that was never told (push was off) is told once.
 */
export function lastNotifiedCondition(fingerprints) {
    let level = null;
    const factors = new Set();
    for (const text of fingerprints) {
        const condition = parseWeatherFingerprint(text);
        if (condition === null)
            continue;
        if (level === null || RANK[condition.level] > RANK[level])
            level = condition.level;
        for (const factor of condition.factors)
            factors.add(factor);
    }
    return level === null ? null : { level, factors: FACTOR_ORDER.filter((f) => factors.has(f)) };
}
/** `true` when a condition that was not told before, or a worse one, makes a notice (the change class is NEW_RISK or WORSE). */
export const isNoticeWorthyChange = (change) => change === "NEW_RISK" || change === "WORSE";
const RANK = { NONE: 0, WATCH: 1, HIGH: 2, SEVERE: 3 };
/** `true` for a level that the product treats as a material risk: HIGH (suggest an alternative) and SEVERE (concierge review). */
export const isMaterialWeatherLevel = (level) => RANK[level] >= RANK.HIGH;
export const weatherChanges = Object.freeze([
    /** The new condition is material and nothing material was known before. */
    "NEW_RISK",
    /** The level went up, or a material level got a new deciding factor. */
    "WORSE",
    /** The same level and the same deciding factors. The numbers may differ. */
    "UNCHANGED",
    /** A material risk that is gone or lower. */
    "CLEARED",
    /** Neither the new nor the old condition is material. */
    "TRIVIAL",
]);
/**
 * Compares the verified condition of this check with the last verified one (`null` when there is none: a first check, or only
 * unverified readings before). It classifies; it does not decide to notify (see `weatherNoticePlan`).
 */
export function classifyWeatherChange(previous, next) {
    const nextMaterial = isMaterialWeatherLevel(next.level);
    const previousMaterial = previous !== null && isMaterialWeatherLevel(previous.level);
    if (!nextMaterial)
        return previousMaterial ? "CLEARED" : "TRIVIAL";
    if (previous === null || !previousMaterial)
        return "NEW_RISK";
    if (weatherFingerprint(previous) === weatherFingerprint(next))
        return "UNCHANGED";
    const newFactor = next.factors.some((factor) => !previous.factors.includes(factor));
    return RANK[next.level] > RANK[previous.level] || newFactor ? "WORSE" : "UNCHANGED";
}
/**
 * Who hears about a material weather risk (CHT-765 step 4). A WATCH or calmer level is an in-app note only. HIGH goes to the customer, and to
 * the concierge when the item is fixed or linked to a request (a change would be consequential: a person must look). SEVERE goes to both.
 * The plan never changes the itinerary: a notice is a notice, and the plan is changed through the normal edit commands.
 */
export function weatherNoticePlan(input) {
    if (input.level === "SEVERE")
        return { customer: true, staff: true, reasonCode: "WEATHER_SEVERE" };
    if (input.level === "HIGH") {
        return {
            customer: true,
            staff: input.fixed || input.linkedToRequest,
            reasonCode: "WEATHER_HIGH",
        };
    }
    return { customer: false, staff: false, reasonCode: null };
}
/** The reasons of an unverified weather result that come from the provider side: the check is retried, and staff see it skipped. */
export const weatherProviderFaults = Object.freeze([
    "PROVIDER_UNAVAILABLE",
    "TIMEOUT",
    "QUOTA",
]);
export const isWeatherProviderFault = (reason) => reason !== null && weatherProviderFaults.includes(reason);
/** The sweep looks at items that start within this many hours (contract section 3.3). */
export const WEATHER_REVALIDATION_HORIZON_HOURS = 48;
/** A stored reading younger than this is not asked for again (contract section 3.3). */
export const WEATHER_REVALIDATION_STALE_HOURS = 6;
